/**
 * PricingSettings — calm admin tool for the platform's monetary dials.
 *
 * Iter-39 simplification (Feb 6, 2026): replaced the prior 559-line "Pricing Lab"
 * which mixed real settings with sandbox/scenario projections. The projection
 * cruft was theater — operators don't tune sliders to fiddle with forecasts;
 * they come here to set numbers and leave.
 *
 * What's here now:
 *   • One row of sliders per category (Subscription / Affiliate / DFY)
 *   • Every slider is wired to a real backend write
 *   • Save/Reset buttons at the bottom — confirm dialog gates the save
 *   • Nothing else. No sandbox toggle, no scenario inputs, no projections
 *
 * The Affiliate Earnings Simulator that prospects use to decide on the $300
 * upgrade lives separately on AffiliateUpgrade.jsx — that one serves a real
 * customer. This page is admin-only.
 *
 * Marketing-copy reminder: any time a price moves on this page, run a sweep
 * for stale strings ("$10/mo", "$300", "$1000", "25%", "30%", etc.) across
 * FanPortal, FAQ, marketing pages, tooltips, and onboarding flows.
 */
import { useEffect, useState, useMemo } from "react";
import { ArrowCounterClockwise, FloppyDisk, Info, Compass, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ConfirmModal } from "@/components/PromptModals";

const fmt$ = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const fmtPct = (n) => `${n}%`;
const fmtN = (n) => String(n);

const Slider = ({ label, value, min, max, step = 1, format = (v) => v, onChange, sublabel, testid }) => (
  <div className="bg-white border hairline rounded-md p-4">
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
        {sublabel && <div className="text-[11px] text-[#6B6A68] mt-0.5">{sublabel}</div>}
      </div>
      <div className="font-serif-display text-xl text-[#1C1B1A] tabular-nums shrink-0">{format(value)}</div>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-[#E5E4E0]
        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#4A5D4E] [&::-webkit-slider-thumb]:shadow-md"
    />
    <div className="flex justify-between text-[10px] text-[#6B6A68] mt-1 tabular-nums">
      <span>{format(min)}</span><span>{format(max)}</span>
    </div>
  </div>
);

const SwitchRow = ({ label, sublabel, value, onChange, testid }) => (
  <div className="bg-white border hairline rounded-md p-4 flex items-start justify-between gap-4">
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
      {sublabel && <div className="text-[11px] text-[#6B6A68] mt-0.5">{sublabel}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={testid}
      aria-pressed={value}
      className={`shrink-0 w-14 h-7 rounded-full relative transition-colors ${value ? "bg-[#4A5D4E]" : "bg-[#D5D4D0]"}`}
    >
      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${value ? "left-[30px]" : "left-0.5"}`} />
    </button>
  </div>
);

export default function PricingSettings() {
  // Live values pulled from /admin/fees + /admin/thresholds at mount.
  const [live, setLive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, onConfirm: null, title: "", message: "" });
  // Feb 19, 2026 — list of users for the cold-signup fallback picker.
  // Pulled from /admin/users on mount. Cheap — already paginated to 2000.
  const [users, setUsers] = useState([]);

  const fetchLive = async () => {
    try {
      // Feb 2026 (Randy pt-batch5f) — also pull the Creator Pro catalog so
      // the Creator Pro gate slider can 2-way bind to catalog.bundle.day1_cents
      // (SAME field the Creator Pro tab writes to). One source of truth.
      const [feesR, thrR, catR] = await Promise.all([
        api.get("/admin/fees"),
        api.get("/admin/thresholds"),
        api.get("/admin/creator-pro/catalog"),
      ]);
      const t = thrR.data.thresholds || {};
      const gate = ((catR.data?.catalog || {}).bundle || {}).day1_cents ?? (t.day_one_bundle_discount_threshold_cents ?? 30000);
      const baseline = {
        platform_fee_cents: feesR.data.platform_fee_cents,
        fan_cut_bps: feesR.data.fan_cut_bps,
        grace_period_days: feesR.data.grace_period_days ?? 14,
        day_one_bundle_discount_threshold_cents: gate,
        affiliate_base_rev_share_pct: t.affiliate_base_rev_share_pct ?? 25,
        affiliate_performance_rev_share_pct: t.affiliate_performance_rev_share_pct ?? 30,
        affiliate_performance_active_customers_threshold: t.affiliate_performance_active_customers_threshold ?? 25,
        done_for_you_cents: t.dfy_price_cents ?? 100000,
        dfy_operator_share_pct: t.dfy_operator_share_pct ?? 70,
        dfy_enabled: !!t.dfy_enabled,
        cold_signup_fallback_referrer_user_id: t.cold_signup_fallback_referrer_user_id ?? "",
      };
      setLive(baseline);
      setDraft(baseline);
    } catch (e) {
      toast.error("Could not load live pricing.");
    }
  };
  useEffect(() => { fetchLive(); }, []);

  // Pull the user roster once for the cold-signup fallback picker. Keep
  // light fields only — id, email, name, role — that's all the dropdown shows.
  useEffect(() => {
    let cancel = false;
    api.get("/admin/users")
      .then((r) => {
        if (cancel) return;
        const lite = (r.data || []).map((u) => ({
          id: u.id, email: u.email, name: u.name, role: u.derived_role || u.role,
        }));
        setUsers(lite);
      })
      .catch(() => { /* picker just shows id-only fallback */ });
    return () => { cancel = true; };
  }, []);

  const dirty = useMemo(() => !!(live && draft && JSON.stringify(live) !== JSON.stringify(draft)), [live, draft]);

  const reset = () => {
    if (!live) return;
    setDraft(live);
    toast("Reverted to saved values.", { icon: "↺" });
  };

  const requestSave = () => {
    if (!draft || !live || !dirty) return;
    const changed = Object.entries(draft)
      .filter(([k, v]) => live[k] !== v)
      .map(([k]) => k);
    setConfirm({
      open: true,
      title: "Apply pricing changes?",
      message: `Saving will update ${changed.length} dial${changed.length === 1 ? "" : "s"} across the live platform. New checkouts will use the new values immediately. Existing Stripe subscriptions keep their current price until renewal.\n\nReminder: after saving, sweep marketing copy + FAQ for stale references to old prices.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          // /admin/fees handles platform_fee_cents + fan_cut_bps + grace_period_days.
          // /admin/thresholds handles everything else.
          const feeUpdates = {};
          if (draft.platform_fee_cents !== live.platform_fee_cents) feeUpdates.platform_fee_cents = draft.platform_fee_cents;
          if (draft.fan_cut_bps !== live.fan_cut_bps) feeUpdates.fan_cut_bps = draft.fan_cut_bps;
          if (draft.grace_period_days !== live.grace_period_days) feeUpdates.grace_period_days = draft.grace_period_days;
          if (Object.keys(feeUpdates).length > 0) await api.patch("/admin/fees", feeUpdates);

          const thrUpdates = {};
          for (const k of [
            "affiliate_base_rev_share_pct", "affiliate_performance_rev_share_pct",
            "affiliate_performance_active_customers_threshold",
            "dfy_operator_share_pct",
            "dfy_enabled",
            "cold_signup_fallback_referrer_user_id",
          ]) {
            if (draft[k] !== live[k]) thrUpdates[k] = draft[k];
          }
          // Special-case: frontend uses "done_for_you_cents" for clarity but
          // backend stores it as "dfy_price_cents".
          if (draft.done_for_you_cents !== live.done_for_you_cents) {
            thrUpdates.dfy_price_cents = draft.done_for_you_cents;
          }
          if (Object.keys(thrUpdates).length > 0) {
            await api.patch("/admin/thresholds", { updates: thrUpdates });
          }
          // Feb 2026 (Randy pt-batch5f) — the "Creator Pro gate price" slider
          // 2-way binds to catalog.bundle.day1_cents (SAME field the
          // Creator Pro tab writes to). Only fire the patch if it changed.
          if (draft.day_one_bundle_discount_threshold_cents !== live.day_one_bundle_discount_threshold_cents) {
            await api.patch("/admin/creator-pro/bundle-price", {
              day1_cents: draft.day_one_bundle_discount_threshold_cents,
            });
          }
          toast.success("Pricing saved to live.");
          await fetchLive();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Save failed.");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  if (!draft) {
    return <div className="text-sm text-[#6B6A68]" data-testid="pricing-settings-loading">Loading pricing…</div>;
  }

  return (
    <div data-testid="pricing-settings">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl">Billing &amp; Earnings</h2>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-2xl">
            Live monetary dials for the whole platform. Changes apply on Save and govern every new checkout.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1 cursor-help"
          title="No sandbox or scenario projections — those live elsewhere. These are the live monetary dials only.">
          <Info size={12} weight="duotone" /> Live values
        </span>
      </div>

      {/* Subscription pricing */}
      <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] mt-2 mb-2">Subscription</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Slider label="Creator monthly fee" value={draft.platform_fee_cents}
          min={0} max={5000} step={100} format={fmt$}
          testid="ps-platform-fee"
          sublabel="Monthly subscription Creators pay to use the platform."
          onChange={(v) => setDraft({ ...draft, platform_fee_cents: v })} />
        <Slider label="Platform fee on fan payments" value={draft.fan_cut_bps}
          min={0} max={2000} step={50} format={(v) => `${(v / 100).toFixed(1)}%`}
          testid="ps-fan-cut"
          sublabel="Your cut of EVERY fan payment — subscriptions AND tips. Since Sep 2026 the supporter pays this on top of the creator's price (the Supporter Service Fee is priced from this rate), so the creator keeps 100% of what they set. (Currently 5%.)"
          onChange={(v) => setDraft({ ...draft, fan_cut_bps: v })} />
        <Slider label="Grace period" value={draft.grace_period_days}
          min={1} max={60} step={1} format={(v) => `${v} day${v === 1 ? "" : "s"}`}
          testid="ps-grace-days"
          sublabel="How long a creator stays active after a failed Stripe payment before their silos pause."
          onChange={(v) => setDraft({ ...draft, grace_period_days: v })} />
      </div>

      {/* Affiliate program. Feb 20, 2026 — Inbound bounty system retired.
          Affiliates now earn their rev-share rate (25%/30%) on EVERY paid
          invoice from their referees, including the gate itself. The
          sliders below are the only knobs that matter. */}
      <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] mt-2 mb-2">Partner Program</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {/* Feb 2026 (Randy pt-batch5f) — this slider 2-way binds to
            catalog.bundle.day1_cents. Editing here PATCHes the Creator Pro
            catalog's bundle price directly (same value the Creator Pro tab
            writes to). One source of truth, two UI entry points.

            Feb 24, 2026 (Randy pt-referral-refactor) — renamed from
            "Creator Pro price" to "Day One Bundle Discount Threshold".
            Constrained to floor $101 (must exceed the $100 Workbench
            base — anything below collapses the bundle into the
            already-paid Workbench SKU and defeats the discount) and
            ceiling $603 (the à-la-carte total — anything above means
            nobody would ever bundle-buy). Backend enforces the same
            range in `platform_thresholds.validate_constraint` so a
            curl can't bypass the UI cap.
            The $100 Workbench floor is FIXED and drives referral
            qualifier eligibility separately — it's not controlled by
            this slider. If you raise Workbench to $150 in the catalog
            editor, the qualifier follows automatically. */}
        <Slider label="Day One Bundle Discount Threshold" value={draft.day_one_bundle_discount_threshold_cents}
          min={10100} max={60300} step={100} format={fmt$}
          testid="ps-day1-bundle-threshold"
          sublabel="Full-bundle upgrade price (2-way tied to Creator Pro tab → Full bundle → Day 1 price). Range: $101 (must exceed the fixed $100 Workbench base) to $603 (the à-la-carte total). Delta pricing applies automatically: a user who already paid $100 for Workbench and buys the bundle here is only charged the difference. The $100 Workbench floor is the referral qualifier and is NOT controlled by this slider — it moves only when you edit the Workbench SKU price."
          onChange={(v) => setDraft({ ...draft, day_one_bundle_discount_threshold_cents: v })} />
        <Slider label="Base rev share" value={draft.affiliate_base_rev_share_pct}
          min={5} max={50} step={1} format={fmtPct}
          testid="ps-affiliate-base-pct"
          sublabel="% of a referee's monthly subscription paid to a base-tier Partner, every month they stay paid."
          onChange={(v) => setDraft({ ...draft, affiliate_base_rev_share_pct: v })} />
        <Slider label="Performance rev share" value={draft.affiliate_performance_rev_share_pct}
          min={5} max={60} step={1} format={fmtPct}
          testid="ps-affiliate-perf-pct"
          sublabel="Bumped rate (replaces base) when a Partner auto-promotes to Performance tier via the thresholds below."
          onChange={(v) => setDraft({ ...draft, affiliate_performance_rev_share_pct: v })} />
        <Slider label="Performance customer threshold" value={draft.affiliate_performance_active_customers_threshold}
          min={5} max={200} step={1} format={fmtN}
          testid="ps-affiliate-perf-customers"
          sublabel="When a Partner has this many active paying referrals, they auto-promote to the Performance tier. 1-customer increments so you can dial in numbers like 33."
          onChange={(v) => setDraft({ ...draft, affiliate_performance_active_customers_threshold: v })} />
      </div>

      {/* Done-for-You */}
      <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] mt-2 mb-2">Done-for-You</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {/* Oct 2026 (Randy) — one switch for the whole offering. OFF hides the
            Fan Portal card and refuses new checkouts; the admin queue, operator
            shares, payouts, refunds and every existing order stay exactly where
            they are, so this is a curtain rather than a demolition. */}
        <SwitchRow
          label="Offer Done-for-You"
          value={!!draft.dfy_enabled}
          testid="ps-dfy-enabled"
          sublabel={draft.dfy_enabled
            ? "ON — the purchase card shows at the bottom of the Fan Portal at the price below."
            : "OFF — hidden from the Fan Portal and new checkouts are refused. Existing orders, payouts and the admin queue are untouched."}
          onChange={(v) => setDraft({ ...draft, dfy_enabled: v })} />
        <Slider label="DFY price" value={draft.done_for_you_cents}
          min={50000} max={500000} step={5000} format={fmt$}
          testid="ps-dfy-price"
          sublabel="One-time fee for a curated twin built by the platform team or a vetted operator."
          onChange={(v) => setDraft({ ...draft, done_for_you_cents: v })} />
        <Slider label="Operator share" value={draft.dfy_operator_share_pct}
          min={50} max={90} step={1} format={fmtPct}
          testid="ps-dfy-operator-pct"
          sublabel="% of the DFY price paid to whoever delivers the twin (rest goes to platform)."
          onChange={(v) => setDraft({ ...draft, dfy_operator_share_pct: v })} />
      </div>

      {/* Attribution — Feb 19, 2026. Cold signups (no ?ref=, no from-silo,
          no cookie trace) get attributed to this user. Set to nobody to keep
          cold signups unattributed (platform-keeps-100% mode). */}
      <div className="mt-8 mb-3 pt-6 border-t-2 border-[#4A5D4E] flex items-center gap-2">
        <Compass size={22} weight="duotone" className="text-[#4A5D4E]" />
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Attribution</h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E]">cold-signup upline</span>
      </div>
      <div className="bg-white border-2 border-[#4A5D4E] rounded-md p-4 mb-6" data-testid="ps-cold-signup-fallback-row">
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">Cold signup fallback referrer</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">
              When someone signs up at <code className="text-[#1C1B1A]">/signup</code> with no referral link, no silo slug, and no prior cookie trace, they're attributed to this user. That user earns the 25% rev share if the new signup pays for a Creator subscription, and the referral commission if they upgrade to Creator Pro. Set to "Nobody" to keep cold signups unattributed (platform keeps 100%).
            </div>
          </div>
        </div>
        <select
          value={draft.cold_signup_fallback_referrer_user_id || ""}
          onChange={(e) => setDraft({ ...draft, cold_signup_fallback_referrer_user_id: e.target.value })}
          data-testid="ps-cold-signup-fallback-select"
          className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
        >
          <option value="">Nobody — keep cold signups unattributed</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}{u.name ? ` · ${u.name}` : ""}{u.role ? ` (${u.role})` : ""}
            </option>
          ))}
        </select>

        {/* Backfill — sweeps existing users who signed up BEFORE the fallback
            was set and attaches them to whatever's currently selected above.
            Idempotent; safe to re-run. */}
        <div className="mt-4 pt-4 border-t hairline">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">Backfill unattributed cold signups</div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Walks every existing user with no upline and attaches them to the fallback currently saved above. Save your fallback choice first. Idempotent — safe to re-run.
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (live?.cold_signup_fallback_referrer_user_id !== draft.cold_signup_fallback_referrer_user_id) {
                  toast.error("Save your fallback choice first, then run the backfill.");
                  return;
                }
                if (!draft.cold_signup_fallback_referrer_user_id) {
                  toast.error("Pick a fallback user above first — backfill needs a target.");
                  return;
                }
                try {
                  const r = await api.post("/admin/cold-signups/backfill", {});
                  const { attached_count, skipped_count, target_user_email } = r.data;
                  toast.success(`Backfilled ${attached_count} user${attached_count === 1 ? "" : "s"} to ${target_user_email}${skipped_count ? ` · ${skipped_count} skipped` : ""}.`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Backfill failed.");
                }
              }}
              data-testid="ps-cold-signup-backfill-btn"
              className="text-xs px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] flex items-center gap-1.5 shrink-0"
            >
              Run backfill
            </button>
          </div>
        </div>
      </div>

      {/* Save / Reset */}
      <div className="flex items-center gap-3 mt-6 pt-4 border-t hairline">
        <button onClick={requestSave} disabled={!dirty || saving}
          data-testid="pricing-settings-save"
          className="px-4 py-2 rounded-md text-sm flex items-center gap-1.5 bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed">
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={reset} disabled={!dirty || saving}
          data-testid="pricing-settings-reset"
          className="px-4 py-2 rounded-md text-sm flex items-center gap-1.5 border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#1C1B1A] disabled:opacity-40 disabled:cursor-not-allowed">
          <ArrowCounterClockwise size={14} weight="duotone" /> Reset
        </button>
        {dirty && (
          <span className="text-[11px] text-[#C16E5A] italic" data-testid="pricing-settings-dirty">Unsaved changes</span>
        )}
      </div>

      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        confirmLabel="Apply to live"
        danger
        onConfirm={async () => { setConfirm({ open: false }); await confirm.onConfirm?.(); }}
        onClose={() => setConfirm({ open: false })}
      />
    </div>
  );
}
